import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { loginHref } from "../../data/auth";
import { clearSession, getSession, logout as logoutApi } from "../../lib/auth";
import { ApiError } from "../../lib/api";
import { getDriverProfile, getDriverSummary } from "../../lib/driverApi";
import type { DriverProfile, DriverSummary } from "../../lib/driverApi";
import type { DriverContext } from "../../lib/driverContext";
import { toast } from "../../lib/toast";
import { cn } from "../../lib/format";
import { getDict, useT } from "../../i18n";
import LanguageSwitcher from "../ui/LanguageSwitcher";
import NotificationBell from "../notifications/NotificationBell";
import AccountMenu from "../ui/AccountMenu";
import SideNav, { SideNavButton } from "../ui/SideNav";
import { useSeo } from "../../lib/seo";

const NAV = [
  { key: "dashboard", href: "/driver" },
  { key: "bookings", href: "/driver/bookings" },
  { key: "documents", href: "/driver/documents" },
  { key: "messages", href: "/driver/messages" },
  { key: "favourites", href: "/driver/favourites" },
  { key: "reviews", href: "/driver/reviews" },
  { key: "profile", href: "/driver/profile" },
] as const;

/** Header + sidebar shell for every /driver page. */
export default function DriverLayout() {
  const t = useT();
  const navigate = useNavigate();
  const [driver, setDriver] = useState<DriverProfile | null>(null);
  const [summary, setSummary] = useState<DriverSummary | null>(null);
  // Phones: sidebar panel opened from the header
  const [menuOpen, setMenuOpen] = useState(false);
  // Account pages stay out of search results
  useSeo({ title: t.seo.driverArea, noindex: true, nofollow: true });

  const refreshSummary = useCallback(() => {
    getDriverSummary().then(setSummary, () => {
      // Badges are optional; keep the last numbers
    });
  }, []);

  // New replies and decisions show up without reloading the page
  useEffect(() => {
    refreshSummary();
    const timer = setInterval(refreshSummary, 30_000);
    return () => clearInterval(timer);
  }, [refreshSummary]);

  const logout = useCallback(async () => {
    await logoutApi();
    toast.success(getDict().auth.toasts.loggedOut);
    navigate(loginHref("driver"));
  }, [navigate]);

  /** Session no longer valid: forget it without calling the API again. */
  const expired = useCallback(() => {
    clearSession();
    navigate(loginHref("driver"));
  }, [navigate]);

  const onLoadError = useCallback(
    (err: unknown) => {
      toast.error(err);
      // Token invalid or refresh failed: log in again.
      if (err instanceof ApiError && err.status === 401) expired();
    },
    [expired],
  );

  /** Re-reads the driver, e.g. after a document upload. */
  const reload = useCallback(
    () => getDriverProfile().then(setDriver, onLoadError),
    [onLoadError],
  );

  useEffect(() => {
    getDriverProfile().then(setDriver, onLoadError);
  }, [onLoadError]);

  const missingDocs =
    summary?.documentsToFix ?? driver?.documents.filter((d) => d.status === "missing" || d.status === "rejected").length ?? 0;
  const badges: Partial<Record<(typeof NAV)[number]["key"], number>> = {
    bookings: summary?.pendingRequests ?? 0,
    documents: missingDocs,
    messages: summary?.unreadMessages ?? 0,
    reviews: summary?.reviewsToWrite ?? 0,
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <header className="sticky top-0 z-30 border-b border-line-strong bg-white">
        {/* Phones: tighter spacing, compact logo, icon-only "Find a vehicle" so the row fits 360 px;
            under 360 px that button hides (the logo leads to the search) so the row fits 300 px */}
        <div className="flex h-14 items-center gap-1.5 px-2.5 min-[360px]:gap-2 min-[360px]:px-3 sm:gap-4 sm:px-6">
          <SideNavButton open={menuOpen} onClick={() => setMenuOpen(true)} />
          <Link to="/" className="shrink-0 whitespace-nowrap text-xs font-semibold tracking-[0.15em] text-ink no-underline sm:text-sm sm:tracking-[0.3em]">
            LE COIN VTC
          </Link>
          <span className="hidden border-l border-line-strong pl-4 text-[13px] text-muted sm:block">
            {t.driverArea.area}
          </span>
          <LanguageSwitcher className="ml-auto" />
          <NotificationBell />
          <Link
            to="/vehicles"
            aria-label={t.driverArea.findVehicle}
            className="hidden h-9 shrink-0 items-center whitespace-nowrap rounded bg-brand px-2.5 text-[13px] font-medium text-white no-underline transition-colors hover:bg-brand-dark min-[360px]:flex sm:px-3.5"
          >
            <Search size={16} className="sm:hidden" aria-hidden="true" />
            <span className="hidden sm:inline">{t.driverArea.findVehicle}</span>
          </Link>
          <AccountMenu
            name={driver ? `${driver.firstName} ${driver.lastName}`.trim() : getSession()?.name || ""}
            email={driver?.email ?? getSession()?.email}
            onLogout={() => void logout()}
          />
        </div>
      </header>

      <div className="grid flex-1 grid-cols-1 content-start items-start lg:grid-cols-[232px_minmax(0,1fr)]">
        <SideNav open={menuOpen} onClose={() => setMenuOpen(false)} label={t.driverArea.nav.aria} title={t.driverArea.area}>
          {NAV.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === "/driver"}
              className={({ isActive }) =>
                cn(
                  "flex items-center justify-between gap-2.5 whitespace-nowrap rounded px-3 py-2.5 text-sm font-medium text-ink no-underline transition-colors hover:bg-[#EDEDED]",
                  // Current page: white with a brand edge, so it stands out from hover
                  isActive && "bg-white text-brand-link shadow-[inset_3px_0_0_var(--color-brand),0_1px_2px_rgba(0,0,0,0.06)] hover:bg-white",
                )
              }
            >
              {t.driverArea.nav[item.key]}
              {!!badges[item.key] && (
                <span className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-brand px-1.25 text-[11px] text-white">
                  {badges[item.key]}
                </span>
              )}
            </NavLink>
          ))}
        </SideNav>

        <main className="w-full max-w-full min-w-0 px-4 pt-5 pb-12 lg:px-9 lg:pt-7 lg:pb-16">
          <Outlet context={{ driver, reload, summary, refreshSummary, setDriver } satisfies DriverContext} />
        </main>
      </div>
    </div>
  );
}
